import { Intro, StatStrip, Friction, HeroStat, StackedBar, Takeaway, SectionLead, Disclaimer, Shot } from './components.jsx';

export const meta = {
  slug: 'fresh-cut-paper',
  metaTitle: 'How FreshCut Paper Avoided $104K in Costs | Account Editor',
  metaDescription:
    'Discover how FreshCut Paper prevented roughly $104K in return costs and resolved 11,074 post-purchase issues without adding support workload.',
  brand: 'FreshCut Paper',
  industry: 'DTC Gifting · Shopify Plus',
  statNum: '~$104K',
  statDesc: 'Return-to-origin costs avoided · 11,074 self-service fixes',
  detail:
    'How FreshCut Paper stopped wrong addresses from turning into return-to-origin shipments — preventing roughly $104K in return costs and resolving 11,074 post-purchase issues without support.',
  quote:
    '"A customer mistypes their address at checkout. Without a fast, accurate fix, that order ships, fails to deliver, and comes back — return-to-origin. You pay for the forward shipping, the return label, and then a reship or refund."',
  author: 'FreshCut Paper Team',
  role: 'Pop-Up Paper Bouquets · Concord, MA',
  img: 'Fresh+Cut+Paper',
  hero: 'FreshCut+Paper+Case+Study',
  image: '/images/case-studies/fresh-cut-paper-case-study.webp',
  readTime: '5 min read',
  date: 'May 25, 2026'
};

export default function Content() {
  return (
    <>
      <Intro>
        Bad addresses don't become returns anymore. FreshCut Paper ships about 107,000 orders a
        month — most of them gifts on a one-business-day deadline. A single mistyped address used
        to mean a return-to-origin shipment and a missed occasion.
      </Intro>

      <StatStrip
        items={[
          { num: '~$104K', label: 'est. return-to-origin costs avoided', highlight: true },
          { num: '5,752', label: 'addresses corrected & validated' },
          { num: '5,329', label: 'unique orders self-served' },
          { num: '11,074', label: 'total self-service fixes' },
        ]}
        period="Jul 2025 – May 2026 · ~10 months"
      />

      <SectionLead eyebrow="About the merchant" title="A high-volume gifting brand where every deadline matters.">
        FreshCut Paper manufactures 3D pop-up paper flower bouquets and cards from a historic mill
        in Concord, MA — shipping roughly 107,000 orders a month, most within one business day,
        and planting a tree with every purchase. When the order is a gift, accuracy at fulfilment
        isn't a nice-to-have.
      </SectionLead>

      <SectionLead eyebrow="The friction" title="The address mistakes that quietly cost you twice.">
        At FreshCut Paper's speed, email-based fixes never catch the order in time. The cost lands
        twice: forward shipping, then return-to-origin.
      </SectionLead>

      <Friction
        question="I typed the wrong address — and it ships today."
        oldWay="Email support, hope someone sees it before pick-and-pack. The order ships, fails to deliver, returns. You pay shipping, return postage, packaging, labour, and a reship."
        newWay="Customer corrects the address on the order page, validated against Google's address database — the warehouse never sees the bad version."
        callout="Customers corrected & validated <strong>5,752</strong> addresses before shipping — the #1 action."
      />

      <Friction
        question="I ordered the wrong one — cancel it before it goes out."
        oldWay="Email support and race the warehouse. If it misses the cut-off, the order ships and the cycle restarts."
        newWay="Customer cancels on the order page before fulfilment. No return, no refund chase."
        callout="Customers self-cancelled <strong>5,091</strong> orders before fulfilment."
      />

      <Friction
        question="My contact details are wrong — I'm not getting any updates."
        oldWay="Wrong email or phone means no shipping or delivery notification — and a 'where is my order?' ticket later."
        newWay="Customer updates email, phone, or shipping method at the source — notifications land, support stays quiet."
        callout="Customers fixed their own details <strong>231</strong> times."
      />

      <SectionLead eyebrow="The result" title="Every wrong address caught before it became a return.">
        Across ~10 months, self-service replaced thousands of failed deliveries and support tickets
        — and the most expensive post-purchase failure stopped happening.
      </SectionLead>

      <Shot
        src="/images/case-studies/fresh-cut-paper-address-editing.webp"
        alt="FreshCut Paper customer correcting their shipping address on the order status page before fulfilment"
        caption="Shoppers fix their own shipping address before the order is picked — no support thread, no failed delivery."
      />

      <HeroStat
        num="~$104K"
        label="estimated return-to-origin costs avoided"
        period="Jul 2025 – May 2026"
        breakdown={[
          { label: 'Addresses corrected', num: '5,752', note: 'validated against Google data' },
          { label: 'Avg cost per return', num: '~$18', note: 'forward + return + reship' },
        ]}
      />

      <StackedBar
        title="The full self-service picture"
        total="11,074"
        totalLabel="total fixes"
        segments={[
          { label: 'Address corrections', value: 5752, tone: 'a' },
          { label: 'Self-cancellations', value: 5091, tone: 'b' },
          { label: 'Contact & detail fixes', value: 231, tone: 'c' },
        ]}
      />

      <Takeaway footnote="Measured across the FreshCut Paper deployment · Jul 2025 – May 2026">
        Wrong addresses, fixed before they shipped. Orders cancelled before fulfilment.{' '}
        <strong>11,074</strong> post-purchase fixes solved by customers themselves — and about{' '}
        <strong>$104K</strong> in returns that never happened.
      </Takeaway>

      <Disclaimer>
        Figures cover Jul 2025 – May 2026. Avoided return cost is modelled as 5,752 corrected
        addresses × ~$18 fully-loaded per-return cost (forward shipping, return postage,
        packaging, labour, reship). Self-service action counts are measured directly from the
        Account Editor deployment.
      </Disclaimer>
    </>
  );
}
